













































































import {
  usePayment,
  useShippingAddress,
  useShippingMethod
} from '@checkout/composables';
import { CartTotalsSectionsEnum } from '@checkout-organisms/order-summary/models';
import type { PropType } from '@nuxtjs/composition-api';
import { computed, defineComponent } from '@nuxtjs/composition-api';
import { SfProperty } from '@storefront-ui/vue';

import { useConfig } from '~/composables';
import { validateFraction } from '~/diptyqueTheme/helpers/priceFormatter';
import cartGetters from '~/helpers/getters/cartGetters';
import type { CartPrices, GiftItem } from '~/models';
import useCart from '~/modules/core/composables/useCart';
import { checkIsNumberType } from '~/modules/core/helpers';

type CartTotalsSectionsType =
  typeof CartTotalsSectionsEnum[keyof typeof CartTotalsSectionsEnum];

export default defineComponent({
  name: 'CartTotals',

  components: {
    CircleLoader: () => import('~/components/CircleLoader.vue'),
    SfProperty,
    Taxes: () => import('molecules/Taxes.vue'),
    Fees: () => import('molecules/Fees.vue'),
    CodFee: () => import('molecules/CodFee.vue')
  },

  props: {
    cartPrices: {
      type: Object as PropType<CartPrices>,
      required: true
    },
    shippingPrice: {
      type: Number,
      default: null
    },
    hideSections: {
      type: Array as PropType<CartTotalsSectionsType[]>,
      default: () => []
    }
  },

  setup(props) {
    const { loading: shippingMethodLoading } = useShippingMethod();
    const { loading: shippingAddressLoading } = useShippingAddress();
    const { loading: paymentMethodLoading } = usePayment();
    const { config } = useConfig();
    const { cart } = useCart();
    const giftMessageSignature =
      config.value?.dptq_sales_cart_signature_gift_row_text;

    const loading = computed(
      () =>
        shippingAddressLoading.saveSelectedShippingAddress.value ||
        shippingMethodLoading.saveSelectedShippingMethod.value ||
        paymentMethodLoading.setPaymentMethod.value
    );

    const totalPrice = computed(
      () =>
        cartGetters.getTotals(
          props.cartPrices,
          config.value?.tax_cart_display_subtotal || ''
        )?.total
    );

    const subtotalPrice = computed(
      () =>
        cartGetters.getTotals(
          props.cartPrices,
          config.value?.tax_cart_display_subtotal || ''
        )?.subtotal
    );

    const totalDiscountValue = computed(() =>
      cartGetters.getDiscountAmount(props.cartPrices)
    );

    const codFeeAmount = computed(() =>
      cartGetters.getCodFeesAmount(props.cartPrices)
    );

    const isTaxDetailsEnabled = computed(
      () =>
        cartGetters.getTaxes(props.cartPrices) &&
        !!config.value?.tax_display_is_tax_details_display
    );

    const isFeesExist = computed(() => cartGetters.getFees(props.cartPrices));

    const isSectionAllowed = (section: CartTotalsSectionsType) => {
      return !props.hideSections.includes(section);
    };

    const giftObject = computed<Record<GiftItem['name'], string>>(() => {
      const items = (props.cartPrices as CartPrices).gift_items || [];
      return items.reduce((acc, item) => {
        acc[item.name] = item.value;
        return acc;
      }, {} as Record<string, string>);
    });

    const hasWelcomeGift = computed(() => !!giftObject.value['welcome_gift']);

    const hasSignatureGift = computed(() => {
      if (!cart.value.gift_wrapping || !cart.value.available_gift_wrappings) {
        return false;
      }
      const currentGiftWrapping = cart.value.available_gift_wrappings.find(
        (gw) => gw.uid === cart.value.gift_wrapping.uid
      );
      return !currentGiftWrapping ? false : !currentGiftWrapping.is_default;
    });

    const getGiftText = (giftName: GiftItem['name']): string => {
      return giftObject.value[giftName] || '';
    };

    return {
      giftMessageSignature,
      CartTotalsSectionsEnum,
      validateFraction,
      checkIsNumberType,
      loading,
      totalPrice,
      subtotalPrice,
      totalDiscountValue,
      codFeeAmount,
      isTaxDetailsEnabled,
      isFeesExist,
      isSectionAllowed,
      giftObject,
      hasWelcomeGift,
      hasSignatureGift,
      getGiftText
    };
  }
});
